// ==============================================
// Profile Page
// ==============================================

import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../services/api';

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();

  // --- Profile info ---
  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [externalProfileUrl, setAmateurProfileLink] = useState(user?.external_profile_url || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // --- Avatar ---
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // --- Password ---
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  if (!user) { 
    return (
      <div className="page">
        <div className="container page-inner">
          <div className="alert alert-warning">Datele utilizatorului nu sunt disponibile.</div>
        </div>
      </div>
    );
  }

  const initials = `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase();
  const siteUrl = process.env.REACT_APP_SITE_URL || '';
  const avatarSrc = avatarPreview || (user.avatar_url ? `${siteUrl}${user.avatar_url}` : null);

  // --- Handlers ---
 
  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarPreview(URL.createObjectURL(file));
    setUploadingAvatar(true);
    try {
      const res = await usersAPI.uploadAvatar(file);
      if (res.success && res.data?.avatar_url) {
        updateUser({ avatar_url: res.data.avatar_url });
      }
    } catch (err: any) {
      setProfileMsg({ type: 'error', text: err.message || 'Eroare la upload imagine' });
      setAvatarPreview(null);
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileMsg(null);
    try {
      await usersAPI.updateProfile({ first_name: firstName, last_name: lastName, phone, external_profile_url: externalProfileUrl });
      updateUser({ first_name: firstName, last_name: lastName, phone, external_profile_url: externalProfileUrl });
      setEditing(false);
      setProfileMsg({ type: 'success', text: 'Profilul a fost actualizat cu succes.' });
    } catch (err: any) {
      setProfileMsg({ type: 'error', text: err.message || 'Eroare la salvare' });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleProfileCancel = () => {
    setFirstName(user.first_name);
    setLastName(user.last_name);
    setPhone(user.phone || '');
    setAmateurProfileLink(user.external_profile_url || '');
    setEditing(false);
    setProfileMsg(null);
  };

  const handlePasswordSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'Parolele noi nu se potrivesc.' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMsg({ type: 'error', text: 'Parola noua trebuie sa aiba cel putin 8 caractere.' });
      return;
    }
    setSavingPassword(true);
    try {
      await usersAPI.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMsg({ type: 'success', text: 'Parola a fost schimbata cu succes.' });
    } catch (err: any) {
      setPasswordMsg({ type: 'error', text: err.message || 'Eroare la schimbarea parolei.' });
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="page">
      <div className="container page-inner" style={{ maxWidth: 720 }}>
        <div className="page-header">
          <h2>Profilul meu</h2>
        </div>

        {/* Avatar */}
        <div className="d-flex justify-content-center mb-4">
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <div
              onClick={() => avatarInputRef.current?.click()}
              style={{
                width: 100, height: 100, borderRadius: '50%',
                overflow: 'hidden', cursor: 'pointer', position: 'relative',
                background: 'var(--tt-primary)', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                fontSize: '2rem', color: '#fff', fontWeight: 700,
                border: '3px solid var(--tt-border)',
              }}
            >
              {avatarSrc
                ? <img src={avatarSrc} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : initials
              }
              {/* Hover overlay */}
              <div style={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', opacity: 0,
                transition: 'opacity .2s',
                color: '#fff', fontSize: '.7rem', fontWeight: 600, gap: 2,
              }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
              >
                {uploadingAvatar
                  ? <span className="spinner-border spinner-border-sm" />
                  : <><span style={{ fontSize: '1.2rem' }}>📷</span><span>Schimba</span></>
                }
              </div>
            </div>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={handleAvatarChange}
            />
          </div>
        </div>

        {/* Profile info card */}
        <div className="entity-card mb-4">
          <div className="entity-card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Informatii personale</h5>
              {!editing && (
                <button className="btn btn-outline-primary btn-sm" onClick={() => { setEditing(true); setProfileMsg(null); }}>
                  Editeaza
                </button>
              )}
            </div>

            {profileMsg && (
              <div className={`alert alert-${profileMsg.type === 'success' ? 'success' : 'danger'} py-2 mb-3`}>
                {profileMsg.text}
              </div>
            )}

            {!editing ? (
              <div className="d-grid gap-2" style={{ gridTemplateColumns: '1fr', rowGap: '.75rem' }}>
                {[
                  { label: 'Prenume',    value: user.first_name },
                  { label: 'Nume',       value: user.last_name },
                  { label: 'Email',      value: user.email },
                  { label: 'Telefon',    value: user.phone || '—' },
                  { label: 'Nivel',      value: user.level_name || '—' },
                  { label: 'Membru din', value: new Date(user.created_at).toLocaleDateString('ro-RO', { year: 'numeric', month: 'long', day: 'numeric' }) },
                ].map(({ label, value }) => (
                  <div key={label} className="d-flex" style={{ borderBottom: '1px solid var(--tt-border)', paddingBottom: '.6rem' }}>
                    <span style={{ minWidth: 130, color: 'var(--tt-text-muted)', fontSize: '.9rem' }}>{label}</span>
                    <span style={{ fontWeight: 500 }}>{value}</span>
                  </div>
                ))}
                <div className="d-flex" style={{ borderBottom: '1px solid var(--tt-border)', paddingBottom: '.6rem' }}>
                  <span style={{ minWidth: 130, color: 'var(--tt-text-muted)', fontSize: '.9rem' }}>Profil extern</span>
                  {user.external_profile_url
                    ? <a href={user.external_profile_url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 500, wordBreak: 'break-all' }}>{user.external_profile_url}</a>
                    : <span style={{ fontWeight: 500 }}>—</span>
                  }
                </div>
              </div>
            ) : (
              <form onSubmit={handleProfileSave}>
                <div className="row g-3 mb-3">
                  <div className="col-sm-6">
                    <label className="form-label">Prenume *</label>
                    <input className="form-control" value={firstName} onChange={e => setFirstName(e.target.value)} required />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label">Nume *</label>
                    <input className="form-control" value={lastName} onChange={e => setLastName(e.target.value)} required />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input className="form-control" value={user.email} disabled />
                  <small className="text-muted">Emailul nu poate fi schimbat.</small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Telefon</label>
                  <input className="form-control" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+40712345678" />
                </div>
                <div className="mb-4">
                  <label className="form-label">
                    URL Profil Extern <span className="text-muted" style={{ fontWeight: 400 }}>(opțional)</span>
                  </label>
                  <input
                    className="form-control"
                    type="url"
                    value={externalProfileUrl}
                    onChange={e => setAmateurProfileLink(e.target.value)}
                    placeholder="https://..."
                  />
                  <small className="text-muted">Ex: Amatur, ETTU, ITTF etc.</small>
                </div>
                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-primary" disabled={savingProfile}>
                    {savingProfile ? <><span className="spinner-border spinner-border-sm me-2" />Se salveaza…</> : 'Salveaza'}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={handleProfileCancel} disabled={savingProfile}>
                    Anuleaza
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Change password card */}
        <div className="entity-card">
          <div className="entity-card-body">
            <h5 className="mb-3">Schimba parola</h5>

            {passwordMsg && (
              <div className={`alert alert-${passwordMsg.type === 'success' ? 'success' : 'danger'} py-2 mb-3`}>
                {passwordMsg.text}
              </div>
            )}

            <form onSubmit={handlePasswordSave}>
              <div className="mb-3">
                <label className="form-label">Parola curenta</label>
                <input
                  className="form-control"
                  type="password"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Parola noua</label>
                <input
                  className="form-control"
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Cel putin 8 caractere"
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="form-label">Confirma parola noua</label>
                <input
                  className="form-control"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={savingPassword}>
                {savingPassword ? <><span className="spinner-border spinner-border-sm me-2" />Se schimba…</> : 'Schimba parola'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
